import { useState } from "react";
import { Check, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { PASSWORD_RULES, passwordProblem } from "@/lib/password";

const input = "w-full px-3 py-2.5 rounded-lg border border-[var(--border)] bg-[var(--surface)] text-sm outline-none focus:border-[var(--brand)]";
const label = "block text-xs uppercase tracking-[0.12em] text-[var(--ink-3)] font-semibold mb-1.5";

/**
 * Choose a new password (POST /auth/set-password). Asks for the current PIN or password unless it was just typed at
 * this sign-in (AuthContext keeps it in memory until here). A live checklist shows the rule; the server is the judge.
 */
export default function PasswordForm({ onDone, submitLabel = "Save password", askCurrent }) {
  const { setPassword, hasPendingSecret } = useAuth();
  const needCurrent = askCurrent ?? !hasPendingSecret();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const problem = passwordProblem(next) || (next !== again ? "The two passwords are not the same." : "");
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError("");
    try {
      const user = await setPassword(next, needCurrent ? current : undefined);
      onDone?.(user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not save the password");
    } finally {
      setBusy(false);
    }
  };

  const type = show ? "text" : "password";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="password-form">
      {needCurrent && (
        <div>
          <label htmlFor="pw-current" className={label}>Current PIN or password</label>
          <input id="pw-current" type="password" autoComplete="current-password" value={current}
                 onChange={(e) => setCurrent(e.target.value)} className={input} required data-testid="pw-current" />
        </div>
      )}
      <div>
        <label htmlFor="pw-new" className={label}>New password</label>
        <div className="relative">
          <input id="pw-new" type={type} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)}
                 className={`${input} pr-11`} required autoFocus={!needCurrent} aria-describedby="pw-rules" data-testid="pw-new" />
          <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 grid place-items-center text-[var(--ink-3)] hover:text-[var(--ink)]">
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <ul id="pw-rules" className="mt-2 space-y-1" aria-live="polite">
          {PASSWORD_RULES.map((r) => {
            const ok = r.test(next);
            return (
              <li key={r.id} className={`flex items-center gap-2 text-xs ${ok ? "text-[var(--moss)]" : "text-[var(--ink-3)]"}`}>
                <span className={`w-4 h-4 rounded-full grid place-items-center border ${ok ? "bg-[var(--moss)] border-[var(--moss)] text-white" : "border-[var(--border)]"}`}>
                  {ok && <Check size={11} />}
                </span>
                {r.label}<span className="sr-only">{ok ? " (done)" : " (still needed)"}</span>
              </li>
            );
          })}
          <li className="text-xs text-[var(--ink-3)] pl-6">Not your name, and not an easy one like Password123</li>
        </ul>
      </div>
      <div>
        <label htmlFor="pw-again" className={label}>Type it again</label>
        <input id="pw-again" type={type} autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)}
               className={input} required data-testid="pw-again" />
      </div>
      {error && (
        <div role="alert" className="p-3 rounded-lg bg-[var(--danger-soft)] border border-[var(--danger)]/20 text-[var(--danger)] text-sm" data-testid="pw-error">
          {error}
        </div>
      )}
      <button type="submit" disabled={busy}
              className="w-full py-2.5 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold disabled:opacity-60" data-testid="pw-submit">
        {busy ? "Saving…" : submitLabel}
      </button>
    </form>
  );
}
